import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { NavLink } from "react-router-dom";
import StockItemPrint from "../../reports/StockItemPrint";
import { useGeneralHooks } from "../../../../../hooks/generalHooks";
//pages & includes
import ManageSidebar from "../ManageSidebar";
import { useReactToPrint } from "react-to-print";

//functions
import {
  _t,
  getCookie,
  formatPrice,
  currencySymbolLeft,
  currencySymbolRight,
  checkPermission
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { MdDelete } from 'react-icons/md';

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import Moment from "react-moment";

//context consumer
import { SettingsContext } from "../../../../../contexts/Settings";
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { UserContext } from "../../../../../contexts/User";
import DataTable from "../../../../common/DataTable";
import CsvImportExport from "../../../../../components/CsvImportExport";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";

const GoodsReceivedNoteHistory = () => {
  const { t } = useTranslation();
  //getting context values here

  const { authUserInfo, getSupplier, supplierForSearch } = useContext(UserContext)

  let {
    //common
    loading,
    setLoading,

    //pagination
    dataPaginating,
    setDataPaginating,

    //goods recieve
    goodReceiveList,
    setGoodReceiveList,
    goodReceiveListForSearch,
    setGoodReceiveListForSearch,
    setPaginatedGoodsRecieveNotes,
    branchForListSearch,
  } = useContext(RestaurantContext);

  // States hook here

  //search result
  let [searchedSupplier, setSearchedSupplier] = useState({
    list: null,
    searched: false,
  });

  const [btnDisable, setBtnDisable] = useState("");

  const searchInputRef = useRef(null);

  //print bills states and function
  const [newRow, setNewRow] = useState([]);
  let [goodsNoteView, setGoodsNoteView] = useState({
    date: null,
    doc_id: null,
    supplier: null,
    branch: '',
    remarks: '',
    purchase_order: null,
    total_amount: '',
    net_amount: "",
    total_discount: "",
    total_tax: ""
  });

  const { enableStockBrand } = useGeneralHooks();

  const printColumns = useMemo(() => {
    const hasExtraCols = Boolean(newRow?.[0]?.total || goodsNoteView?.net_amount);

    const cols = [
      {
        key: "item_name",
        label: _t(t("Name")),
        accessor: "item_name",
        headerClassName: "table_child_ordertype",
      },
    ];

    if (enableStockBrand) {
      cols.push({
        key: "brand_name",
        label: _t(t("Brand")),
        accessor: (row) => row?.brand_name ?? "-",
        headerClassName: "table_child_ordertype",
      });
    }

    cols.push(
      {
        key: "po_qty",
        label: _t(t("PR Qty")),
        accessor: "po_qty",
      },
      {
        key: "qty",
        label: _t(t("Qty")),
        accessor: "qty",
      },
      {
        key: "unit_name",
        label: _t(t("Unit")),
        accessor: "unit_name",
      },
      {
        key: "rate",
        label: _t(t("Rate")),
        accessor: (row) => row?.rate ?? null,
        total: () => _t(t("Total")),
      },
      {
        key: "amount",
        label: _t(t("Amount")),
        accessor: "amount",
        format: "price",
        total: (totals) =>
          totals?.total_amount
            ? parseFloat(totals.total_amount).toFixed(2)
            : "",
      }
    );

    if (hasExtraCols) {
      cols.push(
        {
          key: "total",
          label: _t(t("Total")),
          accessor: (row) => (row?.total !== undefined && row?.total !== null ? formatPrice(row.total) : ""),
          total: (totals) =>
            totals?.net_amount
              ? parseFloat(totals.net_amount).toFixed(2)
              : "",
        }
      );
    }

    return cols;
  }, [enableStockBrand, newRow, goodsNoteView, t]);

  const componentRef = useRef();

  //useEffect == componentDidMount
  useEffect(() => {

    if (!supplierForSearch) {
      getSupplier();
    }

    setPaginatedGoodsRecieveNotes(1)

  }, []);

  const handleSetCsvResponse = useCallback((data) => {
    setGoodReceiveList(data[0]);
    setGoodReceiveListForSearch(data[1]);
    setSearchedSupplier({
      ...searchedSupplier,
      list: data[1],
    });
  }, [])

  //search goods here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedSupplier({ ...searchedSupplier, searched: false });
    } else {
      let searchedList = goodReceiveListForSearch.filter((item) => {
        //name
        let lowerCaseItemSupplierName = item.supplier_name.toLowerCase();
        //branch
        let lowerCaseBranch = item.branch_name.toLowerCase();
        //email
        let lowerCaseItemInvoice = item.document_identity !== null && item.document_identity.toLowerCase();

        return (
          lowerCaseItemSupplierName.includes(searchInput) ||
          lowerCaseItemInvoice.includes(searchInput) ||
          lowerCaseBranch.includes(searchInput)
        );
      });
      setSearchedSupplier({
        ...searchedSupplier,
        list: searchedList,
        searched: true,
      });
    }
  };

  //delete confirmation modal of goods note
  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">{_t(t("You want to delete this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleDeletePurchase(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  //delete goods here
  const handleDeletePurchase = (id) => {
    setLoading(true);
    const url = BASE_URL + `/settings/goods-received-note/${id}`;
    return axios
      .delete(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        handleSetCsvResponse(res.data)

        setLoading(false);
        ToastSuccess({ title: _t(t("Goods Note has been deleted successfully")) })
      })
      .catch(() => {
        setLoading(false);
        ToastError({ title: _t(t("Please try again")) })
      })
  };

  const handlePostApi = (id) => {
    // setDataPaginating(true);
    setBtnDisable(id)
    const url = BASE_URL + `/settings/mark-is-post-grn`;
    const formData = {
      id: id,
      status: 1
    }
    return axios
      .post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        handleSetCsvResponse(res.data);
        setBtnDisable("")
        setDataPaginating(false);
        ToastSuccess({ title: _t(t("Goods received note has been post")) })
      }).catch((err) => {
        setDataPaginating(false);
        setBtnDisable("")
        if (err.response.data?.status === 0) {
          ToastError({ title: _t(t(err.response.data.message)) })
          return;
        }
        ToastError({ title: _t(t("Please try again")) })
      });
  }


  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: "@page { margin: 15px }"
  });


  const handleGetPrintData = async (id) => {
    const url = BASE_URL + "/settings/goods-received-note/" + parseInt(id);
    // setLoading(true)
    try {
      const res = await axios
        .get(url, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
      if (res.data && res.data.goods_received_note_detail) {
        let purchaseDate = new Date(res.data.document_date);
        let suplier = supplierForSearch?.find(item => item?.id === res.data.supplier_id);
        const branch = branchForListSearch[res.data.branch_id];
        setGoodsNoteView({
          ...goodsNoteView,
          date: purchaseDate,
          branch,
          supplier: suplier,
          doc_id: res.data.document_identity,
          remarks: res.data.desc,
          ref_doc: res.data?.ref_document_identity,
          total_amount: res.data.total_amount,
          net_amount: res.data.net_amount,
          total_discount: res.data.total_discount,
          total_tax: res.data.total_tax
        })
        setNewRow([...res.data.goods_received_note_detail]);
        setLoading(false)
        setTimeout(() => {
          handleOnlyPrint();
        }, 0);
      }
    } catch (error) {
      setLoading(false)
      console.log("error==>", error)
      ToastError({ title: _t(t("Please try again")) })
    }
  }

  const COLUMNS = [
    { label: "S/L", render: (item, i) => i + 1 },
    { label: "GRN #", key: "document_identity" },
    { label: "GRN Date", render: (item) => <Moment format="LL">{item.document_date}</Moment> },
    { label: "Branch", key: "branch_name" },
    { label: "Supplier/Vendor", key: "supplier_name" },
    { label: "Ref Document Identity", key: "ref_document_identity" },
    {
      label: "Total", render: (item) => (
        <>
          {currencySymbolLeft()}
          {formatPrice(item.net_amount)}
          {currencySymbolRight()}
        </>
      )
    },
    { label: "Remarks", key: "desc" },
    {
      label: "Status", render: (item) => (
        item.status === "Open" ? <span className="text-success">{item.status}</span> : (item.status === "Closed" ? <span className="text-danger">{item.status}</span> : <>{item.status}</>)
      )
    },
    {
      label: "Action", render: (item) => (
        <div className="d-flex justify-content-center">
          <button
            className="btn btn-secondary ml-2 rounded-sm del-btn"
            onClick={() => handleGetPrintData(item.id)}
          >
            <i className="fa fa-print edit-icon"></i>
          </button>
          {
            item.is_post !== 1 ?
              <>
                <NavLink
                  disabled={item.id === btnDisable}
                  className="btn btn-primary ml-2 rounded-sm"
                  to={`/dashboard/manage/stock/goods-received-note-edit/${item.id}`}
                  style={{ height: "1.7rem", width: "1.7rem" }}
                >
                  <div className="d-flex align-items-center justify-content-center">
                    <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
                  </div>
                </NavLink>
                <button
                  disabled={item.id === btnDisable}
                  className="btn btn-danger ml-2 rounded-sm del-btn"
                  onClick={() => handleDeleteConfirmation(item.id)}
                >
                  <MdDelete className="del-icon" />
                </button>
                {
                  checkPermission(authUserInfo.permissions, "GRN Post") && (
                    <button
                      className="btn btn-dark ml-2 rounded-sm del-btn"
                      title="Post purchase"
                      disabled={loading || item.id === btnDisable}
                      style={{ padding: "1px 0px 0px 1px" }}
                      onClick={() => handlePostApi(item.id)}
                    >
                      <i className="fa fa-check-square-o" aria-hidden="true"></i>
                    </button>
                  )
                }
              </> : ""
          }
        </div>
      )
    },
    { label: "Created Date", render: (item) => <Moment format="LL">{item.created_at}</Moment> },
    { label: "Created By", render: (item) => <>{item?.created_by_name || "-"}</> },
    { label: "Updated By", render: (item) => <>{item?.updated_by_name || "-"}</> },
    { label: "Posted By", render: (item) => <>{item?.post_by_name || "-"}</> },
  ];

  const getListData = useCallback(() => {
    if (searchedSupplier.searched) {
      return searchedSupplier.list;
    }
    return goodReceiveList?.data || [];
  }, [searchedSupplier, goodReceiveList]);

  const handleFetchData = (page) => {
    setPaginatedGoodsRecieveNotes(page);
  }

  const onClearSearch = () => {
    setSearchedSupplier({ ...searchedSupplier, searched: false });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  }

  const csvData = [
    {
      key: "id",
      index: 0,
    },
    {
      key: "branch_name",
      index: 1,
    },
    {
      key: "supplier_name",
      index: 2,
    },
    {
      key: "ref_document_identity",
      index: 3,
    },
    {
      key: "item_name",
      index: 4,
    },
    {
      key: "grn_qty",
      index: 5,
    },
    {
      key: "unit",
      index: 6,
    },
    {
      key: "rate",
      index: 7,
    },
    {
      key: "remarks",
      index: 8,
    }
  ]

  return (
    <>
      <Helmet>
        <title>{_t(t("Ingredient Goods Note"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div key="fragment3">
                <div className="t-bg-white">
                  <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                    <div className="col-md-7 col-lg-6 t-mb-15 mb-md-0 d-flex justify-content-between align-items-center">
                      <ul className="t-list fk-breadcrumb">
                        <li className="fk-breadcrumb__list">
                          <span className="t-link fk-breadcrumb__link text-capitalize">
                            {t("GRN List")}
                          </span>
                        </li>
                      </ul>
                      <CsvImportExport
                        filename="GRN Sample Format CSV"
                        csvLength={9}
                        exportUrl="/settings/export-goods-received-note-csv"
                        importUrl="/settings/upload-goods-received-note-csv"
                        exportBtnName="Sample Format CSV"
                        csvData={csvData}
                        onCsvResponse={handleSetCsvResponse}
                      />
                    </div>
                    <div className="col-md-5 col-lg-6">
                      <div className="row gx-3 align-items-center">
                        {/* Search customer */}
                        <div className="col-md-9 t-mb-15 mb-md-0">
                          <div className="input-group">
                            <div className="form-file">
                              <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control border-0 form-control--light-1 rounded-left"
                                placeholder={_t(t("Search")) + ".."}
                                onChange={handleSearch}
                                disabled={loading || dataPaginating}
                              />
                            </div>
                            <button
                              className="btn btn-primary rounded-right"
                              type="button"
                            >
                              <i
                                className="fa fa-search"
                                aria-hidden="true"
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Add customer modal trigger button */}
                        <div className="col-md-3 text-md-right">
                          <NavLink
                            to="/dashboard/manage/stock/goods-received-note-add"
                            className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                          >
                            {t("add new")}
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <DataTable
                  loading={loading || dataPaginating}
                  data={getListData()}
                  columns={COLUMNS}
                  emptyText="No data available"
                  pagination={{
                    isPageLoading: loading,
                    isPagination: true,
                    pageCount: goodReceiveList?.last_page,
                    currentPage: goodReceiveList?.current_page,
                    from: goodReceiveList?.from,
                    to: goodReceiveList?.to,
                    total: goodReceiveList?.total,
                  }}
                  handleFetchData={handleFetchData}
                  searchPayload={{
                    isSearched: searchedSupplier.searched,
                    onClearSearch
                  }}
                />
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}
      <StockItemPrint
        name="Goods Received Note"
        printDetailView={goodsNoteView}
        columns={printColumns}
        data={newRow}
        totals={goodsNoteView}
        showTotals={true}
        PrintView={componentRef}
        className="print-text-stock"
      />
    </>
  )
}

export default GoodsReceivedNoteHistory
